import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useAuth } from '../AuthContext';
import OfficeBookingDrawer from '../components/OfficeBookingDrawer';
import { useAddPayment } from '../contexts/AddPaymentContext';
import { useTodayData } from '../office/today/useTodayData';
import { LAYOUT_OWNER_EMAIL, useTodayPrefs, type SavedItem } from '../office/today/useTodayPrefs';
import { CARD_BY_ID, CARD_DEFS, DEFAULT_HIDDEN, type CardContext } from '../office/today/registry';
import { PALETTES, QUICK_ACCENTS, accentVars } from '../office/today/palettes';
import { CardBoundary, Icon, OverdueNote } from '../office/today/cards';
import type { TodayAction } from '../office/today/summary';
import type { DrawerActionType } from '../office/today/workflowBoard';
import '../office/today/today.css';

const Grid = WidthProvider(Responsive);
const STACK_BELOW = 760;
// Fine 2px rows with no vertical grid margin; the 14px gap below each card sits inside its slot (see today.css),
// so a card's slot is exactly its content height plus the gap.
const ROW = 2;
const GAP = 14;
const rowsFor = (px: number) => Math.ceil((px + GAP) / ROW);
// Card sizes in the registry are in the old 20px-row units (row + 14px gap = 34px).
const OLD_ROW_PX = 34;

const greetingFor = (d: Date) => (d.getHours() < 12 ? 'Good morning' : d.getHours() < 17 ? 'Good afternoon' : 'Good evening');

type DrawerState = { unitId: string; action: { type: DrawerActionType; nonce: number; unitId: string } | null } | null;

export default function OfficeTodayPage() {
  const { currentProjectId, currentUser } = useAuth();
  const navigate = useNavigate();
  const { openAddPayment } = useAddPayment();
  // Hidden switch on the booked count: off = booked by status (Units page), on = also count booking forms. Always starts off.
  const [includeForms, setIncludeForms] = useState(false);
  const { loading, error, summary: s, costs, projectMeta, units, reload } = useTodayData(currentProjectId, includeForms);
  const { prefs, update, reset, orgDefault, saveAsDefault } = useTodayPrefs(currentUser?.uid);
  const isLayoutOwner = String(currentUser?.email || '').trim().toLowerCase() === LAYOUT_OWNER_EMAIL;
  const [defaultMsg, setDefaultMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [drawerOffset, setDrawerOffset] = useState(0);
  const [editing, setEditing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const gridHostRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<Record<string, number>>({});
  const [width, setWidth] = useState(1200);
  const now = useMemo(() => new Date(), []);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const projectName = String(projectMeta?.name || projectMeta?.projectName || '').trim();
  const firstName = String(currentUser?.displayName || '').trim().split(/\s+/)[0];
  const greeting = `${greetingFor(now)}${firstName ? `, ${firstName}` : ''}`;
  const dateLabel = now.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const drawerUnit = drawer ? units.find((u) => u.id === drawer.unitId) || null : null;
  const stacked = width < STACK_BELOW;

  // A user's own arrangement wins; otherwise the shared default set by the layout owner; otherwise the built-in one.
  const baseLayout = prefs.layout || orgDefault?.layout || null;
  const hidden = useMemo(
    () => new Set(prefs.layout ? prefs.hidden : orgDefault ? orgDefault.hidden : DEFAULT_HIDDEN),
    [orgDefault, prefs.hidden, prefs.layout]
  );
  const layout: SavedItem[] = useMemo(() => {
    const saved = new Map((baseLayout || []).map((l) => [l.i, l]));
    let bottom = (baseLayout || []).reduce((m, l) => Math.max(m, l.y + l.h), 0);
    return CARD_DEFS.filter((c) => !hidden.has(c.id)).map((c) => {
      const own = saved.get(c.id);
      if (own) return own;
      if (!baseLayout) return { i: c.id, ...c.layout };
      const placed = { i: c.id, x: 0, y: bottom, w: c.layout.w, h: c.layout.h };
      bottom += c.layout.h;
      return placed;
    });
  }, [baseLayout, hidden]);
  const ordered = useMemo(() => layout.slice().sort((a, b) => a.y - b.y || a.x - b.x), [layout]);
  // Each card is exactly as tall as its content, so nothing scrolls inside a card and no card leaves an
  // empty gap at its bottom. The saved height is only used until the content has been measured.
  const gridLayouts = useMemo(() => {
    const sized = (l: SavedItem) => {
      const fallback = Math.round(((CARD_BY_ID.get(l.i)?.layout.h || 8) * OLD_ROW_PX) / ROW);
      const h = fit[l.i] || fallback;
      return { ...l, h, minW: CARD_BY_ID.get(l.i)?.layout.minW, minH: h, maxH: h };
    };
    const lg: Layout[] = layout.map(sized);
    // Tablet: 6 columns, wide cards take the full row and small ones pair up, in the same order.
    let cursor = 0;
    const md: Layout[] = ordered.map((l, idx) => {
      const w = l.w >= 6 ? 6 : 3;
      const x = w === 6 ? 0 : cursor;
      cursor = w === 6 ? 0 : (x + w) % 6;
      return { ...sized(l), w, x, y: idx, minW: Math.min(w, 3) };
    });
    return { lg, md };
  }, [fit, layout, ordered]);

  useEffect(() => {
    const host = gridHostRef.current;
    if (!host) return;
    // A short timer rather than requestAnimationFrame: frames pause in background tabs and panes.
    let timer: number | undefined;
    const measure = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const next: Record<string, number> = {};
        host.querySelectorAll<HTMLElement>('.ot-gi').forEach((gi) => {
          const card = gi.querySelector<HTMLElement>(':scope > .ot-card');
          const last = card?.lastElementChild as HTMLElement | null;
          if (!card || !last || !gi.dataset.id) return;
          const cs = getComputedStyle(card);
          next[gi.dataset.id] = rowsFor(last.offsetTop + last.offsetHeight + parseFloat(cs.paddingBottom) + parseFloat(cs.borderBottomWidth));
        });
        setFit((prev) => (Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next));
      }, 30);
    };
    const mo = new MutationObserver(measure);
    mo.observe(host, { subtree: true, childList: true, characterData: true });
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    measure();
    return () => {
      mo.disconnect();
      ro.disconnect();
      window.clearTimeout(timer);
    };
  }, [loading, stacked, s]);

  const saveLayout = (next: Layout[]) =>
    update({ layout: next.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })), hidden: Array.from(hidden) });
  const hideCard = (id: string) => update({ layout: layout.filter((l) => l.i !== id), hidden: [...Array.from(hidden), id] });
  const addCard = (id: string) => {
    const bottom = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    const def = CARD_BY_ID.get(id);
    if (!def) return;
    update({ layout: [...layout, { i: id, x: 0, y: bottom, w: def.layout.w, h: def.layout.h }], hidden: Array.from(hidden).filter((h) => h !== id) });
  };

  const openStep = (unitId: string, type: DrawerActionType | null) => setDrawer({ unitId, action: type ? { type, nonce: Date.now(), unitId } : null });
  const openUnit = (unitId: string) => openStep(unitId, null);
  const onAction = (a: TodayAction) => {
    if (a.path) return navigate(a.path);
    if (!a.unitId) return;
    if (a.kind === 'call') return openUnit(a.unitId);
    const task = s?.workflow.tasks.find((t) => a.id === `wf:${t.key}`);
    openStep(a.unitId, task?.steps[0]?.action?.type || null);
  };

  const rootClass = `ot${editing && !stacked ? ' ot-editing' : ''}`;
  const rootStyle: React.CSSProperties = { ...accentVars(prefs.accent), paddingRight: drawer && drawerOffset > 0 ? drawerOffset + 20 : undefined };

  if (!currentProjectId) {
    return (
      <div className={rootClass} data-palette={prefs.palette} style={rootStyle} ref={rootRef}>
        <div className="ot-card ot-blank">
          <h3 style={{ margin: '0 0 6px' }}>Pick a project</h3>
          <p className="muted" style={{ margin: 0 }}>Choose a project from the project picker to see its numbers for today.</p>
        </div>
      </div>
    );
  }

  // Opens the same Add payment drawer as Banks, set to a customer receipt; Today updates live once it is saved.
  const recordReceipt = () => openAddPayment({ defaultTxnType: 'receipt', receiptCategory: 'customer' });
  const ctx: CardContext | null = s ? { s, costs, recordReceipt, greeting, go: (p) => navigate(p), openUnit, openStep, onAction } : null;
  const hiddenDefs = CARD_DEFS.filter((c) => hidden.has(c.id));

  return (
    <div className={rootClass} data-palette={prefs.palette} style={rootStyle} ref={rootRef}>
      <header className="ot-bar">
        <div>
          <h1>{projectName ? `${projectName} today` : 'Today'}</h1>
          <div className="ot-sub">
            <span className="mono">{dateLabel}</span>
            {s ? (
              <>
                <span className="faint">·</span>
                <span>{s.inventory.total} units</span>
                <span className="faint">·</span>
                <button className="ot-plain" onClick={() => setIncludeForms((v) => !v)}>{s.bookedUnits} booked</button>
              </>
            ) : null}
          </div>
        </div>
        <div className="ot-bar-actions">
          <button className="ot-btn" aria-pressed={editing} onClick={() => setEditing((v) => !v)}>
            <svg className="ot-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" />
            </svg>
            {editing ? 'Done' : 'Customize'}
          </button>
        </div>
      </header>

      {editing ? (
        <div className="ot-panel-tray">
          <div className="ot-tray-row">
            <b>Colour</b>
            <div className="ot-seg wide" role="group" aria-label="Colour scheme">
              {PALETTES.map((p) => (
                <button key={p.id} aria-pressed={prefs.palette === p.id && !prefs.accent} onClick={() => update({ palette: p.id, accent: null })}>
                  <span className="ot-sw" style={{ background: p.swatch, boxShadow: `inset -5px 0 0 ${p.panel}`, marginRight: 6, verticalAlign: -1 }} />
                  {p.label}
                </button>
              ))}
            </div>
            <label className="ot-pick" data-on={!!prefs.accent}>
              <input type="color" value={prefs.accent || '#e8642a'} onChange={(e) => update({ accent: e.target.value })} aria-label="Pick your own accent colour" />
              Own colour
            </label>
            <span className="ot-swatches" role="group" aria-label="Quick accent colours">
              {QUICK_ACCENTS.map((c) => (
                <button key={c} style={{ background: c }} aria-label={`Accent ${c}`} aria-pressed={prefs.accent === c} onClick={() => update({ accent: c })} />
              ))}
            </span>
            {prefs.accent ? <button className="ot-btn sm" onClick={() => update({ accent: null })}>Use scheme colour</button> : null}
          </div>
          <div className="ot-tray-row">
            <b>Add a card</b>
            {hiddenDefs.length ? hiddenDefs.map((c) => (
              <button key={c.id} className="ot-chipbtn" onClick={() => addCard(c.id)}><Icon name="plus" />{c.name}</button>
            )) : <span className="ot-tray-hint">Every card is on the page.</span>}
            <button className="ot-btn sm" onClick={reset} style={{ marginLeft: 'auto' }}>{orgDefault ? 'Reset to default' : 'Reset layout'}</button>
          </div>
          {isLayoutOwner ? (
            <div className="ot-tray-row">
              <b>Default</b>
              <button
                className="ot-btn sm dark"
                onClick={async () => {
                  setDefaultMsg(null);
                  try {
                    await saveAsDefault(layout, Array.from(hidden), String(currentUser?.email || ''));
                    setDefaultMsg({ ok: true, text: 'Saved. Everyone who has not arranged their own page now starts from this layout.' });
                  } catch (err) {
                    console.error('Office Today: set default layout failed', err);
                    setDefaultMsg({ ok: false, text: "Couldn't save the default. The new Firestore rule may not be deployed yet." });
                  }
                }}
              >
                Set as default for everyone
              </button>
              <span className="ot-tray-hint" style={{ color: defaultMsg ? (defaultMsg.ok ? 'var(--ot-good-ink)' : 'var(--ot-bad-ink)') : undefined }}>
                {defaultMsg ? defaultMsg.text : 'Only you see this. It saves the cards and their order and sizes; colours stay personal.'}
              </span>
            </div>
          ) : null}
          <span className="ot-tray-hint">
            {stacked
              ? 'Moving and resizing cards works on a wider screen. Colours apply everywhere.'
              : 'Drag a card by its handle to move it, drag the bottom-right corner to resize, or hide it. Your layout and colours are saved to your account.'}
          </span>
        </div>
      ) : null}

      {error ? <div className="ot-alert" role="alert">{error}</div> : null}

      {loading || !ctx ? (
        <div className="ot-grid" aria-busy="true" aria-label="Loading today's numbers">
          <div className="ot-skel ot-s6" style={{ height: 300 }} />
          <div className="ot-stats ot-s6">{[0, 1, 2, 3].map((i) => <div key={i} className="ot-skel" style={{ height: 143 }} />)}</div>
          <div className="ot-skel ot-s8" style={{ height: 300 }} />
          <div className="ot-skel ot-s4" style={{ height: 300 }} />
        </div>
      ) : stacked ? (
        <div className="ot-stack">
          {ordered.map((l, i) => {
            const def = CARD_BY_ID.get(l.i);
            return def ? <CardBoundary key={l.i} name={def.name}>{def.render(ctx, i)}</CardBoundary> : null;
          })}
        </div>
      ) : (
        <div ref={gridHostRef}>
        <Grid
          className="ot-rgl"
          layouts={gridLayouts}
          breakpoints={{ lg: 1100, md: 0 }}
          cols={{ lg: 12, md: 6 }}
          rowHeight={ROW}
          margin={[GAP, 0]}
          containerPadding={[14, 0]}
          isDraggable={editing}
          isResizable={editing}
          draggableHandle=".ot-grip"
          compactType="vertical"
          onDragStop={(l) => width >= 1100 && saveLayout(l)}
          onResizeStop={(l) => width >= 1100 && saveLayout(l)}
        >
          {ordered.map((l, i) => {
            const def = CARD_BY_ID.get(l.i);
            if (!def) return null;
            return (
              <div key={l.i} className="ot-gi" data-id={l.i}>
                <div className="ot-editbar">
                  <span className="ot-grip" aria-label={`Drag to move ${def.name}`} title="Drag to move">
                    <svg className="ot-ic" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      {[6, 12, 18].map((y) => <React.Fragment key={y}><circle cx="9" cy={y} r="1.5" /><circle cx="15" cy={y} r="1.5" /></React.Fragment>)}
                    </svg>
                  </span>
                  <span className="name">{def.name}</span>
                  <button className="ot-hide" onClick={() => hideCard(l.i)} aria-label={`Hide ${def.name}`} title="Hide card">
                    <svg className="ot-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
                  </button>
                </div>
                <CardBoundary name={def.name}>{def.render(ctx, i)}</CardBoundary>
              </div>
            );
          })}
        </Grid>
        </div>
      )}
      {ctx && ctx.s.dues.noPlanUnits ? <div style={{ padding: '10px 6px 0' }}><OverdueNote s={ctx.s} /></div> : null}

      <OfficeBookingDrawer
        open={!!drawer && !!drawerUnit}
        onClose={() => {
          setDrawer(null);
          setDrawerOffset(0);
          reload();
        }}
        onDesktopOffsetChange={(px) => setDrawerOffset(px)}
        projectId={currentProjectId}
        unit={drawerUnit as any}
        onOpenUnitPage={() => drawer && navigate(`/office/units/${drawer.unitId}`, { state: { fromOfficeToday: true } })}
        actionRequest={drawer?.action || null}
        actionOnly={!!drawer?.action}
      />
    </div>
  );
}
